// src/context/FirebaseContext.js
import React, { createContext, useContext } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc,
  orderBy,
  addDoc,
  onSnapshot, // 👈 Added missing import
  limit,
  getDoc // 👈 Added for franchisee functions
} from 'firebase/firestore';
import { db, storage } from '../utils/firebase';
import { v4 as uuidv4 } from 'uuid';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const FirebaseContext = createContext();

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};

const getOrderByNumber = async (orderNumber) => {
  try {
    console.log('🔍 Searching for order:', orderNumber);
    
    // חיפוש בקולקציית ההזמנות
    const ordersRef = collection(db, 'orders'); // שנה את השם בהתאם לקולקציה שלך
    const q = query(
      ordersRef, 
      where('orderNumber', '==', orderNumber),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const orderDoc = querySnapshot.docs[0];
      const orderData = {
        id: orderDoc.id,
        ...orderDoc.data()
      };
      
      console.log('✅ Order found:', orderData.orderNumber);
      return orderData;
    } else {
      console.log('❌ Order not found:', orderNumber);
      return null;
    }
  } catch (error) {
    console.error('Error fetching order by number:', error);
    throw error;
  }
};

const updateOrder = async (orderId, updateData) => {
  try {
    console.log('📋 Updating order:', orderId);
    const orderRef = doc(db, 'orders', orderId);
    await updateDoc(orderRef, updateData);
    console.log('✅ Order updated successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Error updating order:', error);
    return { success: false, error };
  }
};

export const FirebaseProvider = ({ children }) => {
  
  // Real-time listener for direct orders
  const getDirectOrdersListener = (userBranches, onSuccess, onError) => {
    if (!userBranches || userBranches.length === 0) {
      console.log('No branches provided for listener');
      onSuccess([]);
      return () => {}; // Return empty unsubscribe function
    }

    try {
      // Extract branch IDs from userBranches
      const branchIds = userBranches.map(branch => 
        typeof branch === 'string' ? branch : branch.id || branch.branchId
      ).filter(Boolean);

      if (branchIds.length === 0) {
        console.log('No valid branch IDs found');
        onSuccess([]);
        return () => {};
      }

      console.log('🔄 Setting up real-time listener for branches:', branchIds);

      // Create query for direct orders
      const ordersRef = collection(db, 'orders'); // 👈 Changed to 'orders' based on your existing queries
      const q = query(
        ordersRef,
        where('branchId', 'in', branchIds),
        orderBy('createdAt', 'desc') // Order by creation date, newest first
      );

      // Set up the real-time listener
      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          const orders = [];
          
          querySnapshot.forEach((doc) => {
            const orderData = {
              id: doc.id,
              ...doc.data(),
              // Ensure dates are properly converted
              createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
              customerOrderDate: doc.data().customerOrderDate?.toDate?.() || new Date(doc.data().customerOrderDate),
              updatedAt: doc.data().updatedAt?.toDate?.() || new Date(doc.data().updatedAt)
            };
            
            orders.push(orderData);
          });

          console.log('📱 Real-time update:', orders.length, 'orders received');
          
          // Call success callback with the orders
          onSuccess(orders);
        },
        (error) => {
          console.error('❌ Real-time listener error:', error);
          onError(error);
        }
      );

      // Return the unsubscribe function for cleanup
      return unsubscribe;

    } catch (error) {
      console.error('Error setting up real-time listener:', error);
      onError(error);
      return () => {};
    }
  };

  // Get branches for user
  const getUserBranches = async (userData) => {
    try {
      console.log('📋 Getting branches for user:', userData.belongs);
      
      const branchesRef = collection(db, 'branches');
      let q;
      
      if (userData.belongs === 'זכיין') {
        console.log('🏢 Querying branches for franchiseeId:', userData.franchiseeId);
        q = query(branchesRef, where('franchiseeId', '==', userData.franchiseeId));
      } else if (userData.belongs === 'מנהל סניף') {
        console.log('🏪 Querying single branch:', userData.branchId);
        q = query(branchesRef, where('__name__', '==', userData.branchId));
      }
      
      const querySnapshot = await getDocs(q);
      const branches = [];
      
      querySnapshot.forEach((doc) => {
        branches.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log('✅ Found branches:', branches.length);
      return branches;
    } catch (error) {
      console.error('❌ Error getting branches:', error);
      throw error;
    }
  };

  // 🆕 NEW: Get all branches belonging to a specific franchisee
  const getFranchiseeBranches = async (franchiseeId) => {
    try {
      if (!franchiseeId) {
        console.error('No franchiseeId provided');
        return [];
      }

      console.log('🏢 Getting branches for franchisee:', franchiseeId);
      const branchesRef = collection(db, 'branches');
      const q = query(branchesRef, where('franchiseeId', '==', franchiseeId));
      const querySnapshot = await getDocs(q);
      
      const branches = [];
      querySnapshot.forEach((doc) => {
        const branchData = doc.data();
        branches.push({
          id: doc.id,
          name: branchData.branchName || branchData.name || 'סניף ללא שם',
          streetName: branchData.streetName || branchData.address || '',
          franchiseeId: branchData.franchiseeId,
          ...branchData // Include all other branch data if needed
        });
      });

      console.log(`✅ Found ${branches.length} branches for franchisee ${franchiseeId}`);
      return branches;
    } catch (error) {
      console.error('❌ Error fetching franchisee branches:', error);
      throw new Error('Failed to load franchisee branches');
    }
  };

  // 🆕 NEW: Link an order to a specific branch
  const linkOrderToBranch = async (orderId, branchId, branchDisplayName) => {
    try {
      if (!orderId || !branchId) {
        throw new Error('Missing orderId or branchId');
      }

      console.log('🔗 Starting linkOrderToBranch:', { orderId, branchId, branchDisplayName });

      // Update the order document
      const orderRef = doc(db, 'orders', orderId);
      
      // Check if order exists first
      console.log('📄 Checking if order exists...');
      const orderDoc = await getDoc(orderRef);
      if (!orderDoc.exists()) {
        throw new Error(`Order ${orderId} not found`);
      }
      
      const orderData = orderDoc.data();
      console.log('✅ Order found, current data:', orderData);

      // Get branch details for complete update
      console.log('🏢 Getting branch details...');
      const branchRef = doc(db, 'branches', branchId);
      const branchDoc = await getDoc(branchRef);
      
      if (!branchDoc.exists()) {
        throw new Error(`Branch ${branchId} not found`);
      }
      
      const branchData = branchDoc.data();
      console.log('✅ Branch found:', branchData);

      // Prepare update data - CHANGE STATUS TO "הועבר לסניף"
      const updateData = {
        // Standard order fields that other parts of the app expect
        branchId: branchId,
        branchName: branchData.branchName || branchData.name || 'סניף ללא שם',
        
        // CHANGE STATUS - this will move it from tender to direct orders
        status: 'הזמנה',
        
        // Additional linking metadata
        linkedToBranchId: branchId,
        linkedToBranchName: branchDisplayName,
        linkedAt: new Date().toISOString(),
        linkedBy: 'franchisee',
        
        // Update timestamp
        updatedAt: new Date().toISOString()
      };
      
      console.log('📝 Updating order with data:', updateData);

      // Update order document
      await updateDoc(orderRef, updateData);

      console.log(`✅ Successfully updated order ${orderId} in Firebase`);
      console.log('📋 Order status changed to: הועבר לסניף');
      
      // Verify the update by reading the document again
      const updatedOrderDoc = await getDoc(orderRef);
      if (updatedOrderDoc.exists()) {
        const updatedData = updatedOrderDoc.data();
        console.log('🔍 Verification - Updated order data:', {
          branchId: updatedData.branchId,
          branchName: updatedData.branchName,
          status: updatedData.status,
          linkedToBranchId: updatedData.linkedToBranchId,
          linkedToBranchName: updatedData.linkedToBranchName
        });

        // 📲 Send WhatsApp notifications to branch users
        await sendBranchNotifications(branchData, orderData, updatedData);
      }
      
      return { success: true, orderId, branchId, branchDisplayName, statusChanged: true };
    } catch (error) {
      console.error('❌ Error in linkOrderToBranch:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw new Error(`Failed to link order to branch: ${error.message}`);
    }
  };

  // 📲 NEW: Send WhatsApp notifications to branch users
  const sendBranchNotifications = async (branchData, orderData, updatedOrderData) => {
    try {
      console.log('📲 Sending WhatsApp notifications to branch users...');
      
      // Import WhatsApp service dynamically to avoid import issues
      const { default: WhatsAppService } = await import('../services/WhatsAppService');
      
      // Collect phone numbers from branch data
      const phoneNumbers = [];
      
      if (branchData.branchMobile) {
        phoneNumbers.push(branchData.branchMobile);
      }
      if (branchData.branchPhone) {
        phoneNumbers.push(branchData.branchPhone);
      }
      if (branchData.branchPartnerPhone) {
        phoneNumbers.push(branchData.branchPartnerPhone);
      }
      
      console.log('📞 Found phone numbers:', phoneNumbers);
      
      if (phoneNumbers.length === 0) {
        console.log('⚠️ No phone numbers found for branch notifications');
        return;
      }
      
      // Prepare notification message
      const cleanAddress = branchData.fullAddress?.replace(/,?\s*ישראל\s*$/g, '').trim();
      const branchInfo = `🏪 סניף: ${branchData.branchName || branchData.name}${cleanAddress ? ` (${cleanAddress})` : ''}`;

      const message = `🔔 הזמנה חדשה הועברה לסניף!

            📋 מספר הזמנה: ${orderData.orderNumber || 'לא זמין'}
            🏢 ארגון: ${orderData.organizationName || 'לא זמין'}
            ${branchInfo}

            📅 מועד אספקה: ${orderData.customerOrderDate ? new Date(orderData.customerOrderDate).toLocaleDateString('he-IL') : 'לא זמין'}
            💰 סכום: ₪${(orderData.totalAmount || 0) - (orderData.totalProfit || 0)}

            ההזמנה ממתינה לטיפולכם באפליקציה! 📱`;

      // Send to all phone numbers
      const notificationPromises = phoneNumbers.map(async (phoneNumber) => {
        try {
          console.log(`📲 Sending notification to: ${phoneNumber}`);
          const result = await WhatsAppService.sendMessage(phoneNumber, message);
          
          if (result.success) {
            console.log(`✅ WhatsApp sent successfully to ${phoneNumber}:`, result.messageId);
          } else {
            console.error(`❌ Failed to send WhatsApp to ${phoneNumber}:`, result.error);
          }
          
          return result;
        } catch (error) {
          console.error(`❌ Error sending WhatsApp to ${phoneNumber}:`, error);
          return { success: false, error: error.message };
        }
      });
      
      // Wait for all notifications to complete
      const results = await Promise.allSettled(notificationPromises);
      
      const successCount = results.filter(result => 
        result.status === 'fulfilled' && result.value.success
      ).length;
      
      console.log(`📲 WhatsApp notifications completed: ${successCount}/${phoneNumbers.length} successful`);
      
    } catch (error) {
      console.error('❌ Error sending branch notifications:', error);
      // Don't throw error here - linking should still succeed even if notifications fail
    }
  };

  // 🆕 NEW: Unlink an order from a branch (optional)
  const unlinkOrderFromBranch = async (orderId, branchId) => {
    try {
      if (!orderId) {
        throw new Error('Missing orderId');
      }

      console.log('🔓 Unlinking order', orderId, 'from branch');

      const orderRef = doc(db, 'orders', orderId);
      
      // Remove branch linking information from order
      await updateDoc(orderRef, {
        linkedToBranchId: null,
        linkedToBranchName: null,
        unlinkedAt: new Date().toISOString()
      });

      console.log(`✅ Successfully unlinked order ${orderId} from branch`);
      return { success: true, orderId };
    } catch (error) {
      console.error('❌ Error unlinking order from branch:', error);
      throw new Error(`Failed to unlink order from branch: ${error.message}`);
    }
  };

  // 🆕 NEW: Get orders linked to a specific branch (useful for branch managers)
  const getBranchLinkedOrders = async (branchId) => {
    try {
      if (!branchId) {
        throw new Error('No branchId provided');
      }

      console.log('📋 Getting orders linked to branch:', branchId);

      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('linkedToBranchId', '==', branchId));
      const querySnapshot = await getDocs(q);
      
      const linkedOrders = [];
      querySnapshot.forEach((doc) => {
        linkedOrders.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log(`✅ Found ${linkedOrders.length} orders linked to branch ${branchId}`);
      return linkedOrders;
    } catch (error) {
      console.error('❌ Error fetching branch linked orders:', error);
      throw new Error('Failed to load branch linked orders');
    }
  };

  // Calculate distance between coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Get city from address
  const getCityFromAddress = (address) => {
    if (!address) return '';
    const parts = address.split(',');
    return parts.length > 1 ? parts[1].trim() : '';
  };

  // Get tender orders
  const getTenderOrders = async (userBranches) => {
    try {
      console.log('📋 Getting tender orders...');
      
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const orders = [];
      querySnapshot.forEach((doc) => {
        orders.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log('📦 Total orders found:', orders.length);

      const filteredOrders = orders.filter(order => {
        // First check if procurementType is מכרז
        if (order.procurementType !== 'מכרז') {
          return false;
        }

        const orderCity = getCityFromAddress(order.fullAddress);
        
        return userBranches.some(branch => {
          const branchCity = getCityFromAddress(branch.fullAddress);
          
          if (orderCity && branchCity && orderCity === branchCity) {
            return true;
          }
          
          if (order.latitude && order.longitude && 
              branch.latitude && branch.longitude) {
            const distance = calculateDistance(
              parseFloat(order.latitude),
              parseFloat(order.longitude),
              parseFloat(branch.latitude),
              parseFloat(branch.longitude)
            );
            return distance <= 15;
          }
          
          return false;
        });
      });

      console.log('✅ Filtered tender orders (מכרז only):', filteredOrders.length);
      return filteredOrders;
    } catch (error) {
      console.error('❌ Error getting tender orders:', error);
      throw error;
    }
  };

  // Get direct orders
  const getDirectOrders = async (userBranches) => {
    try {
      console.log('📋 Getting direct orders...');
      
      const branchIds = userBranches.map(branch => branch.id);
      const orders = [];

      for (const branchId of branchIds) {
        console.log('🏪 Querying orders for branch:', branchId);
        
        const ordersRef = collection(db, 'orders');
        const q = query(
          ordersRef, 
          where('branchId', '==', branchId),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          orders.push({
            id: doc.id,
            ...doc.data()
          });
        });
      }

      console.log('✅ Direct orders found:', orders.length);
      return orders;
    } catch (error) {
      console.error('❌ Error getting direct orders:', error);
      throw error;
    }
  };

  // Get statuses
  const getStatuses = async () => {
    try {
      console.log('📋 Getting statuses...');
      
      const statusRef = collection(db, 'status');
      const q = query(statusRef, orderBy('statusID', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const statuses = [];
      querySnapshot.forEach((doc) => {
        statuses.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log('✅ Statuses loaded:', statuses.length);
      return statuses;
    } catch (error) {
      console.error('❌ Error getting statuses:', error);
      throw error;
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId, statusId) => {
    try {
      console.log('📋 Updating order status:', orderId, statusId);
      
      const statusDoc = await getDocs(
        query(collection(db, 'status'), where('statusID', '==', statusId))
      );
      
      if (statusDoc.empty) {
        throw new Error('Status not found');
      }
      
      const statusData = statusDoc.docs[0].data();
      
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: statusData.statusName,
        updatedAt: new Date(),
      });
      
      console.log('✅ Order status updated to:', statusData.statusName);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating order status:', error);
      throw error;
    }
  };

  return (
    <FirebaseContext.Provider value={{
      // Existing functions
      getUserBranches,
      getTenderOrders,
      getDirectOrders,
      getDirectOrdersListener,
      getStatuses,
      updateOrderStatus,
      updateOrder,
      getOrderByNumber,
      
      // 🆕 NEW: Franchisee functions
      getFranchiseeBranches,
      linkOrderToBranch,
      unlinkOrderFromBranch,
      getBranchLinkedOrders,
      
      db // Direct access if needed
    }}>
      {children}
    </FirebaseContext.Provider>
  );
};